import React, { useState } from 'react';
import { Row } from 'components/Layout';
import Spacing from 'components/Spacing';
import PageSection from 'components/PageSection';
import Button from 'components/Button';
import ButtonGroup from 'components/ButtonGroup';
import { LpFeeCommitmentStatus } from 'further-types/lp/lp-fee-template';
import { CurrencyCode } from 'further-types/lp/vehicle';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import { useLayoutContext } from 'contexts/LayoutContext';
import TransactionAllocator from 'components/TransactionAllocator';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import { sumBy } from 'lodash';
import PageLoader from 'components/PageLoader';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import { ApprovalSummaryData } from '..';
import AlertDialog from 'components/AlertDialog';
import { useDisclosure } from 'further-ui/hooks';
import { ListLpFeesCategory } from 'further-types/lp/lp-fee';
import FeesSummaryDisplay from 'components/FeesSummaryDisplay';
import { CalendarDay } from 'further-ui/utils';

const confirmDialogContent = {
  [ListLpFeesCategory.DueUnapprovedFees]: {
    title: 'Confirm Approval',
    message:
      'Are you sure? Fees should only be approved when all quarterly actions have been processed, as actions within the quarter may affect the fee total.',
  },
  [ListLpFeesCategory.FutureUnapprovedFees]: {
    title: 'Confirm Approval',
    message:
      'Are you sure you want to approve these now? These fees are not yet due and other transactions between now and the due date may affect the fee amount.',
  },
};

const ApproveManagementFeesSummary: React.FC<{
  approvalSummaryData: ApprovalSummaryData;
  setApprovalSummaryData: (data: ApprovalSummaryData) => void;
}> = ({ approvalSummaryData, setApprovalSummaryData }) => {
  const { date, fullFeeData, defaultTransactions, feeType } =
    approvalSummaryData;
  const { selectedVehicleId } = useLayoutContext();
  const { vehicle, isLoading: vehicleLoading } = useVehicle(selectedVehicleId);
  const { transactionTypeOptions, isLoading: transactionTypeOptionsLoading } =
    useTransactionTypeSelectorOptions();
  const confirmDialog = useDisclosure();
  const mutationOpts = useHandleTrpcMutation();
  const approveFeesMutation = api.lpFee.approve.useMutation(
    mutationOpts({
      successMessage: 'Fees approved successfully',
      onSuccess: () => {
        setApprovalSummaryData({
          date: '',
          fullFeeData: [],
          defaultTransactions: {
            insideCommitment: [],
            outsideCommitment: [],
          },
          feeType,
        });
      },
    }),
  );

  const { insideCommitmentFees, outsideCommitmentFees } = fullFeeData.reduce<{
    insideCommitmentFees: ApprovalSummaryData['fullFeeData'];
    outsideCommitmentFees: ApprovalSummaryData['fullFeeData'];
  }>(
    (acc, feeData) => {
      if (
        feeData.insideOutsideCommitment ===
        LpFeeCommitmentStatus.InsideCommitment
      ) {
        acc.insideCommitmentFees.push(feeData);
      } else {
        acc.outsideCommitmentFees.push(feeData);
      }
      return acc;
    },
    {
      insideCommitmentFees: [],
      outsideCommitmentFees: [],
    },
  );

  const [insideCommitmentTransactions, setInsideCommitmentTransactions] =
    useState<ApprovalSummaryData['defaultTransactions']['insideCommitment']>(
      insideCommitmentFees.length > 0
        ? defaultTransactions.insideCommitment.map((transaction) => ({
            ...transaction,
            amountIncVatTcy: sumBy(insideCommitmentFees, 'feeIncTaxLcy'),
            amountExVatTcy: sumBy(insideCommitmentFees, 'feeExTaxLcy'),
          }))
        : [],
    );

  const [outsideCommitmentTransactions, setOutsideCommitmentTransactions] =
    useState<ApprovalSummaryData['defaultTransactions']['outsideCommitment']>(
      outsideCommitmentFees.length > 0
        ? defaultTransactions.outsideCommitment.map((transaction) => ({
            ...transaction,
            amountIncVatTcy: sumBy(outsideCommitmentFees, 'feeIncTaxLcy'),
            amountExVatTcy: sumBy(outsideCommitmentFees, 'feeExTaxLcy'),
          }))
        : [],
    );

  if (transactionTypeOptionsLoading || vehicleLoading || !vehicle || !feeType) {
    return <PageLoader />;
  }

  const title = ` management fees paid by [${vehicle.vehicleName}] to the fund manager`;

  const handleBackToFeeManager = () => {
    setApprovalSummaryData({
      date: '',
      fullFeeData: [],
      defaultTransactions: {
        insideCommitment: [],
        outsideCommitment: [],
      },
      feeType: undefined,
    });
  };

  const handleApproveFees = () => {
    approveFeesMutation.mutate({
      vehicleId: vehicle._id.toString(),
      insideCommitment: {
        transactions: insideCommitmentTransactions,
        lpFees: insideCommitmentFees,
      },
      outsideCommitment: {
        transactions: outsideCommitmentTransactions,
        lpFees: outsideCommitmentFees,
      },
    });
  };

  return (
    <>
      <FeesSummaryDisplay
        vehicle={vehicle}
        feeData={fullFeeData}
        feesDate={new CalendarDay(date)}
        title="Fee Breakdown"
        showSummarySection
      />

      {insideCommitmentFees.length > 0 && (
        <PageSection>
          <TransactionAllocator
            title={`Inside commitment` + title}
            vehicle={vehicle}
            transactions={insideCommitmentTransactions}
            transactionTypeOptions={transactionTypeOptions}
            transactionCurrency={CurrencyCode.GBP}
            fxRate={null}
            onChange={(transactions) => {
              setInsideCommitmentTransactions(transactions);
            }}
          />
        </PageSection>
      )}

      {outsideCommitmentFees.length > 0 && (
        <PageSection>
          <TransactionAllocator
            title={`Outside commitment` + title}
            vehicle={vehicle}
            transactions={outsideCommitmentTransactions}
            transactionTypeOptions={transactionTypeOptions}
            transactionCurrency={CurrencyCode.GBP}
            fxRate={null}
            onChange={(transactions) => {
              setOutsideCommitmentTransactions(transactions);
            }}
          />
        </PageSection>
      )}

      <PageSection>
        <Spacing>
          <Row centered justify="space-between">
            <div />
            <ButtonGroup>
              <Button variant="outlined" onClick={handleBackToFeeManager}>
                Back to Fee Manager
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={confirmDialog.onOpen}
              >
                Approve and Process Fees
              </Button>
            </ButtonGroup>
          </Row>
        </Spacing>
      </PageSection>
      <AlertDialog
        open={confirmDialog.isOpen}
        onClose={confirmDialog.onClose}
        title={confirmDialogContent[feeType].title}
        content={confirmDialogContent[feeType].message}
        btnLabels={{
          confirm: 'Confirm',
          cancel: 'Cancel',
        }}
        onConfirm={handleApproveFees}
        confirmBtnProps={{
          disabled: approveFeesMutation.isPending,
          loading: approveFeesMutation.isPending,
        }}
      />
    </>
  );
};

export default ApproveManagementFeesSummary;
